/**
 * WHICH VERSION OF THE WORDS IS THE ONE A JOB SHEET WILL FREEZE, worked out here so the Settings
 * screen and the door agree about it.
 *
 * `api/src/jobsheet/sheet.ts` picks the scope a sheet quotes with one predicate: approved, in
 * force on the day, highest version, and a version written for that home beats the standard one.
 * The screen that publishes these has to say the same thing, or the office reads "in force" over a
 * version no cleaner will ever see. So the predicate is written out once, here, with the door's
 * own rule beside it, and tested rather than trusted.
 *
 * THE THREE STATES ARE NOT DECORATION. A version nobody has approved adjudicates nothing, which is
 * the whole difference between a draft and a scope: the schema pairs the approval time with the
 * approver's name, and an unapproved row is invisible to every door in the product. A version
 * approved but dated ahead is a decision already taken about a day that has not come. And a
 * version that is neither is the record of what a clean was judged against last month, which is
 * why nothing is ever edited and every change is a new number.
 */
import { SERVICE_LINE_LABELS, type ServiceLine } from '@fk/shared';
import type { ScopeRow } from './api.ts';
import { addDays, formatDay } from './format.ts';

export interface ScopeStanding {
  readonly serviceLine: ServiceLine;
  /** What a sheet frozen today would quote. Null means no sheet opens for this line at all. */
  readonly inForce: ScopeRow | null;
  /** Approved and dated ahead: a decision already taken, about a day that has not come. */
  readonly ahead: readonly ScopeRow[];
  /** Written, unapproved, adjudicating nothing. Newest first. */
  readonly drafts: readonly ScopeRow[];
  /** What earlier sheets were judged against. Newest first. */
  readonly superseded: readonly ScopeRow[];
  /** Versions written for one home rather than for the line. Newest first. */
  readonly tailored: readonly ScopeRow[];
  /**
   * HOW MANY HOMES, NOT HOW MANY VERSIONS, and only the homes a cleaner would actually read them
   * at. Versions are the design here, so a home whose words have been revised twice is two rows
   * and one home, and a sentence counting rows says two homes have their own words over a product
   * where one does. A version nobody has approved is worse than a miscount: `scopeFor` in
   * `api/src/jobsheet/sheet.ts` filters on the approval before it prefers a home's row, so a draft
   * counted here claims a home is being cleaned to words no door can see.
   */
  readonly tailoredHomes: number;
}

/** The door's own predicate, character for character: approved, started, and not yet ended. */
export function isInForce(scope: ScopeRow, today: string): boolean {
  return (
    scope.approvedAt !== null &&
    scope.effectiveFrom <= today &&
    (scope.effectiveTo === null || scope.effectiveTo > today)
  );
}

const newestFirst = (rows: readonly ScopeRow[]): ScopeRow[] =>
  [...rows].sort((left, right) => right.version - left.version);

export function scopeStanding(
  scopes: readonly ScopeRow[],
  serviceLine: ServiceLine,
  today: string,
): ScopeStanding {
  const mine = scopes.filter((scope) => scope.serviceLine === serviceLine);
  const standard = newestFirst(mine.filter((scope) => scope.siteId === null));
  const inForce = standard.find((scope) => isInForce(scope, today)) ?? null;
  const tailored = newestFirst(mine.filter((scope) => scope.siteId !== null));
  const homes = new Set(
    tailored.filter((scope) => isInForce(scope, today)).map((scope) => scope.siteId),
  );
  return {
    serviceLine,
    inForce,
    ahead: standard.filter((scope) => scope.approvedAt !== null && scope.effectiveFrom > today),
    drafts: standard.filter((scope) => scope.approvedAt === null),
    superseded: standard.filter(
      (scope) => scope !== inForce && scope.approvedAt !== null && scope.effectiveFrom <= today,
    ),
    tailored,
    tailoredHomes: homes.size,
  };
}

export function scopeStandings(
  scopes: readonly ScopeRow[],
  lines: readonly ServiceLine[],
  today: string,
): ScopeStanding[] {
  return lines.map((line) => scopeStanding(scopes, line, today));
}

/* ------------------------------------------------------------------ *
 * Ending a version, which is the one thing this table could not do.
 * ------------------------------------------------------------------ */

/**
 * WHETHER A VERSION CAN BE ENDED, AND THE WORDS THAT SAY WHY NOT.
 *
 * A higher version supersedes a lower one for the standard words, so the line always quoted the
 * newest approved row and nobody missed this. A version written for ONE HOME is the case that
 * needed it: `scopeFor` in `api/src/jobsheet/sheet.ts` prefers a home's own row over the standard
 * one, and with `effective_to` never set that preference had no end. Words tailored for a spring
 * clean at one address went on beating the standard ones at that address for ever, and the only
 * remedy in the product was to write a new tailored version saying the standard thing.
 *
 * THE LAST DAY IS TODAY, NEVER THIS MOMENT. `effective_to` is exclusive, the same way `isInForce`
 * above reads it, so the day sent is tomorrow: every sheet opened today still freezes what it was
 * always going to freeze, and the change lands on a boundary rather than in the middle of somebody
 * doing the round. A retirement that took effect mid-morning would mean two cleaners at two homes
 * on one day were judged against two different documents.
 *
 * AND THE SENTENCE HAS TO BE TRUE OF THE WHOLE ROUND, which took two corrections. `scopeFor`
 * prefers a home's own row before it looks at the standard one, so ending the standard words while
 * a home has its own shuts nothing at that home, and the confirming question said no job sheet
 * opened at all. The mirror of it: tailored versions are numbered per site, so a home can have two,
 * and ending the lower one sends nobody back to the standard words because the higher one was
 * already what that home quoted. Both sentences now name what actually adjudicates tomorrow.
 *
 * FOUR THINGS ARE REFUSED, and each refusal is a sentence rather than a missing button. A version
 * nobody approved adjudicates nothing, so there is nothing to end. A version already ended cannot
 * end twice. A version that has not started has no time in force to cut short: ending it would
 * mean an `effective_to` at or before its own `effective_from`, which is not a retirement, it is a
 * version that never existed, and this product has no way to say that about a row somebody
 * approved. And the standard words may not be ended with nothing published behind them, which is
 * the fourth and the only one that is the DOOR'S refusal rather than this screen's caution:
 * `retireScope` in `api/src/jobsheet/scopes.ts` answers `nothing_stands_behind_it`, because
 * `scopeFor` would then find no version for any home, `openSheet` would refuse every visit not
 * already frozen, and the start and finish buttons sit under the sheet on that same screen. One
 * press in the office and no cleaner could open a door screen anywhere, with nothing saying so.
 * That case used to be drawn here as an offer whose confirming question PROMISED that outcome, and
 * the press came back a refusal, which is the shape this module exists to prevent: a screen making
 * a claim about tomorrow morning that the door does not agree with.
 */
export interface RetireOffer {
  readonly can: boolean;
  /** Why it can or cannot be ended, in one sentence. Always said, never implied by an absence. */
  readonly why: string;
  /** The words on the control. Null when there is nothing to press. */
  readonly words: string | null;
  /** The confirm step's question, which names what stops quoting these words and what does not. */
  readonly question: string | null;
  /** The day `effective_to` is set to, exclusive, so today is the last day. Null when refused. */
  readonly effectiveTo: string | null;
}

/** A day for a person, falling back to the stored one rather than to the word null. */
const day = (value: string): string => formatDay(value) ?? value;

const refuse = (why: string): RetireOffer => ({
  can: false,
  why,
  words: null,
  question: null,
  effectiveTo: null,
});

/**
 * The other version that adjudicates on the day this one stops, or null.
 *
 * Highest version wins, because that is how `scopeFor` picks between live rows, so the answer is
 * the row a sheet would actually quote rather than any row that happens to qualify.
 */
function standsOnAfter(rows: readonly ScopeRow[], scope: ScopeRow, on: string): ScopeRow | null {
  return newestFirst(rows.filter((row) => row.id !== scope.id && isInForce(row, on)))[0] ?? null;
}

/** The standard versions of this line, the retiring one included, as one list. */
const standardVersions = (standing: ScopeStanding): ScopeRow[] =>
  [standing.inForce, ...standing.ahead, ...standing.superseded].filter(
    (row): row is ScopeRow => row !== null,
  );

/** How many homes have words of their own still in force on the day the standard ones stop. */
function homesOfTheirOwn(standing: ScopeStanding, scope: ScopeRow, on: string): number {
  return new Set(
    standing.tailored
      .filter((row) => row.id !== scope.id && isInForce(row, on))
      .map((row) => row.siteId),
  ).size;
}

/** What an ending of the standard words does not reach, named rather than counted. */
const homeWords = (count: number): string =>
  count === 1 ? 'the one home with words of its own' : `the ${count} homes with words of their own`;

export function retireOffer(standing: ScopeStanding, scope: ScopeRow, today: string): RetireOffer {
  if (scope.approvedAt === null) {
    return refuse(
      'Nobody has approved these words, so no job sheet quotes them and there is nothing to end.',
    );
  }
  if (scope.effectiveTo !== null) {
    return refuse(`These words already stop on ${day(scope.effectiveTo)}.`);
  }
  if (scope.effectiveFrom > today) {
    return refuse(
      `These words do not take over until ${day(scope.effectiveFrom)}, so there is nothing in force to end yet.`,
    );
  }

  const effectiveTo = addDays(today, 1);
  const line = SERVICE_LINE_LABELS[scope.serviceLine].toLowerCase();
  const kept =
    'A job sheet frozen before then still quotes exactly what it showed, which is the whole reason a version is never edited.';

  if (scope.siteId !== null) {
    // ANOTHER VERSION AT THE SAME HOME IS AN ORDINARY THING: `nextVersion` numbers tailored rows
    // per site, so a home whose words were revised has two, and `scopeFor` takes the highest live
    // one. Ending the lower of them changes nothing at all, and telling the office that home goes
    // back to the standard words would be a sentence about a thing that did not happen.
    const alsoHers = standsOnAfter(
      standing.tailored.filter((row) => row.siteId === scope.siteId),
      scope,
      effectiveTo,
    );
    if (alsoHers) {
      return {
        can: true,
        why: `Version ${alsoHers.version} is written for that home as well, so ending these leaves that one quoted there.`,
        words: 'Retire these words',
        question: `Retire version ${scope.version}? Today is its last day, and version ${alsoHers.version} is what that home's job sheets quote tomorrow, not the standard ${line} words. ${kept}`,
        effectiveTo,
      };
    }
    return {
      can: true,
      why: 'These words beat the standard ones at that home until they are ended.',
      words: 'Retire these words',
      question: `Retire version ${scope.version}? Today is its last day, and that home goes back to the standard ${line} words tomorrow. ${kept}`,
      effectiveTo,
    };
  }

  // THE SAME QUESTION THE DOOR ASKS, and asked of the same rows: is some OTHER approved standard
  // version of this line live on the day these stop. Standard only, exactly as `standardStandsOn`
  // reads it, because a version written for one home answers for that home and for no other, so it
  // is not what the rest of the round falls back to.
  const covered = standsOnAfter(standardVersions(standing), scope, effectiveTo) !== null;
  if (!covered) {
    // AND THE HOMES WITH WORDS OF THEIR OWN ARE NOT SHUT BY THIS. `scopeFor` prefers a home's own
    // row before it ever looks at the standard one, so those homes would go on opening sheets
    // exactly as they do. Saying no sheet opens at all would be telling the office the whole round
    // had stopped when part of it had not, which is a sentence that stays wrong in a refusal.
    const homes = homesOfTheirOwn(standing, scope, effectiveTo);
    const except = homes === 0 ? '' : ` anywhere but at ${homeWords(homes)}`;
    return refuse(
      `Nothing else is approved and in force after these, so they cannot be ended yet: no job sheet would open for ${line}${except}, and no clean could be started or finished. Publish the words that replace them, approve them, and then end these.`,
    );
  }

  return {
    can: true,
    why: 'Another approved version takes over the day these stop.',
    words: 'Retire these words',
    question: `Retire version ${scope.version}? Today is its last day, and the next approved version takes over tomorrow. ${kept}`,
    effectiveTo,
  };
}
